import React from "react";
import { useQuery } from "@tanstack/react-query";
import { http } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Phone, Shield, Flame, Stethoscope, Globe2, AlertOctagon } from "lucide-react";

const ICONS = {
  general: AlertOctagon,
  police: Shield,
  fire: Flame,
  medical: Stethoscope,
  immigration: Globe2,
  embassy: Globe2,
  weather: AlertOctagon,
  civil: Shield,
};

export default function EmergencyContacts() {
  const { t } = useI18n();
  const { data } = useQuery({
    queryKey: ["emergency"],
    queryFn: async () => (await http.get("/emergency")).data,
  });

  const contacts = data || [];

  return (
    <section id="emergency" data-testid="emergency-contacts" className="clay p-6 md:p-7">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="eyebrow text-[color:var(--emergency)]">{t("emergency")}</p>
          <h3 className="text-2xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>{t("emergency")} · Nicaragua</h3>
        </div>
        <div className="w-12 h-12 rounded-2xl bg-[color:var(--emergency)]/10 text-[color:var(--emergency)] flex items-center justify-center">
          <AlertOctagon className="w-6 h-6" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {contacts.map((c) => {
          const Icon = ICONS[c.category] || Phone;
          const isCritical = c.number === "911" || c.number === "115" || c.number === "128" || c.number === "118";
          return (
            <a
              key={c.id}
              href={`tel:${c.number.replace(/\s+/g, "")}`}
              data-testid={`emergency-contact-${c.id}`}
              className={`flex items-start gap-3 rounded-2xl p-3.5 border transition-all ${
                isCritical
                  ? "bg-[color:var(--emergency)] text-white border-transparent hover:bg-[color:var(--emergency)]/90"
                  : "bg-white border-[color:var(--hairline)] hover:border-[color:var(--coral)] hover:bg-[color:var(--sand-2)]"
              }`}
            >
              <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                isCritical ? "bg-white/20 text-white" : "bg-[color:var(--sand-2)] text-[color:var(--navy)]"
              }`}>
                <Icon className="w-5 h-5" />
              </span>
              <span className="flex-1 min-w-0">
                <p className="font-semibold leading-tight" style={{ fontFamily: "Outfit" }}>{c.name}</p>
                <p className={`mono font-bold text-base mt-0.5 ${isCritical ? "text-white" : "text-[color:var(--coral)]"}`}>
                  {c.number}
                </p>
                <p className={`text-xs mt-0.5 ${isCritical ? "text-white/80" : "text-[color:var(--ink-2)]"}`}>
                  {c.note}
                </p>
              </span>
            </a>
          );
        })}
      </div>
    </section>
  );
}
